import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { faCircleQuestion } from '@fortawesome/pro-solid-svg-icons';
import { useSettings } from 'services/settings/SettingsContext';
import { SubscriptionPackage } from '../BillingTypes';

interface SeatsSelectionProps {
    activeUsers: { normal: string; field?: string; light?: string };
    enteredSeats: { normal: string; field?: string; light?: string };
    packages: SubscriptionPackage[];
    hasFieldLicenses?: boolean;
    hasLightLicenses?: boolean;
    selectedPackage: string;
    selectedDuration: string;
    setEnteredSeats: (value: { normal: string; field?: string; light?: string }) => void;
}

const SeatsSelection: React.FC<SeatsSelectionProps> = ({ 
    activeUsers, 
    enteredSeats,
    packages, 
    hasFieldLicenses,
    hasLightLicenses, 
    selectedPackage, 
    selectedDuration, 
    setEnteredSeats 
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const [error, setError] = useState<boolean>(false);

    // Parse the value
    const parse = (val?: string) => parseInt(val || "0", 10);

    // Update the seats
    const updateSeats = (type: 'normal' | 'field' | 'light', value: string) => {
        const numericValue = parse(value);
        const minValue = parse(activeUsers[type]);

        let finalValue = value;

        // Show error if the given seats are lower than the active seats
        if (numericValue < minValue) {
            setError(true);

            // Force the value to the minimum
            finalValue = String(minValue);
        } else {
            setError(false);
        }

        // Update the seats state
        setEnteredSeats({ ...enteredSeats, [type]: finalValue });
    };


    // Handle incrementation of the seats field
    const handleIncrement = (type: 'normal' | 'field' | 'light') => {
        const current = parse(enteredSeats[type]);
        updateSeats(type, String(current + 1));
    };

    // Handle decrementation of the seats field
    const handleDecrement = (type: 'normal' | 'field' | 'light') => {
        const current = parse(enteredSeats[type]);
        const min = parse(activeUsers[type]);

        if (current > min) {
            updateSeats(type, String(current - 1));
        }
    };

    // Determine if the decrementation is disabled
    const isDecrementDisabled = (type: 'normal' | 'field' | 'light') => {
        return parse(enteredSeats[type]) <= parse(activeUsers[type]);
    };

    // Get the right label with the right price
    const getLabelWithPrice = (type: 'normal' | 'field' | 'light'): string => {

        // Configure the package label based on the type
        const label = t(`billing.general.${type}_seats_label`);

        // Select the current selected package object
        const selectedPackageObject = packages.find(pkg => pkg.key === selectedPackage);

        // When a package is found, return the label and price
        if (selectedPackageObject) {

            // Get the normal seat price based on the selected duration
            let price = selectedDuration === 'yearly' ? Number(selectedPackageObject.yearly_price) : Number(selectedPackageObject.monthly_price);

            // For field seats, get the field seat price based on the selected duration
            if (type === 'field') {
                price = selectedDuration === 'yearly' ? Number(selectedPackageObject.yearly_field_price) : Number(selectedPackageObject.monthly_field_price);
            };

            // For light seats, get the light seat price based on the selected duration
            if (type === 'light') {
                price = selectedDuration === 'yearly' ? Number(selectedPackageObject.yearly_light_price) : Number(selectedPackageObject.monthly_light_price);
            };

            // Check wheter the price has decimals. If the total has one decimal, convert it to two decimals
            const hasDecimals = price % 1 !== 0;
            const decimalPlaces = hasDecimals
                ? (price.toString().split('.')[1]?.length === 1 ? 2 : 2)
                : 0;

            // Format the amount based on the user locale
            const formattedPrice = new Intl.NumberFormat(userLocale, {
                style: "decimal",
                minimumFractionDigits: decimalPlaces,
                maximumFractionDigits: decimalPlaces,
            }).format(price);

            return `${label} (€${formattedPrice}/${t('billing.general.per_month_short_label')})`;

        // If no package is found, just return the label
        } else {
            return label;
        };
    };

    // Render the seat selection rows
    const renderRow = (type: 'normal' | 'field' | 'light') => (
        <div key={type}>
            <div className='seat-manage-row'>
                <div className='license-info'>
                    <div className='label'>
                        {getLabelWithPrice(type)}
                        {type === 'light' && (
                            // Show a tooltip which explains the light license variants
                            <span className='tooltip-icon'>
                                <FontAwesomeIcon icon={faCircleQuestion} />
                                <span className='tooltip'>{t('billing.general.light_seats_tooltipText')}</span>
                            </span>
                        )}
                    </div>
                    <div className='description'>
                        {t(`billing.general.${type}_seats_description`)}
                    </div>
                </div>
                <div className='controls'>
                    <div 
                        className={`stepper-button ${isDecrementDisabled(type) ? 'disabled' : ''}`}
                        onClick={() => handleDecrement(type)}
                    >
                        <FontAwesomeIcon icon={faMinus} />
                    </div>
                    <input
                        type='text'
                        id={type}
                        value={enteredSeats[type] || ''}
                        onChange={(e) => {
                            const value = e.target.value;

                            if (value === '' || /^[0-9]+$/.test(value)) {
                                updateSeats(type, value);
                            }
                        }}
                        onKeyDown={(e) => { if (['.', ',', 'e'].includes(e.key)) e.preventDefault() }} // Prevent entering decimals and exponentials
                        className='seats-input'
                    />
                    <div 
                        className='stepper-button'
                        onClick={() => handleIncrement(type)}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </div>
                    
                </div>
                {parseInt(activeUsers[type] || '0') > 0 && (
                    <div className='info'>
                        {t('billing.general.active_users', { activeUsers: activeUsers[type] })}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className='container-filled seats-selection'>
            <div className='seats-header'>
                <h3>
                    {t('billing.general.seats_amount_label')}
                </h3>
                <p className='seats-description'>
                    {t('billing.general.seats_amount_description')}
                </p>
            </div>
            <div className='seats-selection-container'>
                <div className='seat-manage-rows'>
                    {renderRow('normal')}
                    {hasFieldLicenses && renderRow('field')}
                    {hasLightLicenses && renderRow('light')}
                </div>
                {error && (
                    <div className='error'>
                        {t('billing.general.minimal_seats_error')} 
                    </div>
                )}
            </div>
        </div>
    )
};

export default SeatsSelection;